import { z } from 'zod';
import { skillSchema } from './agent';

export const skillPromptInputSchema = skillSchema.pick({
  key: true,
  name: true,
  description: true,
  instructions: true,
  examples: true,
});
export type SkillPromptInput = z.infer<typeof skillPromptInputSchema>;

export interface DelegationTargetPromptInput {
  name: string;
  skillKeys?: string[];
}

/** Merge the agent base prompt with assigned skill instructions and examples. */
export function buildAgentSystemPrompt(
  base: string,
  skills: SkillPromptInput[],
  _assignedToolKeys: string[] = [],
  assignedIntegrationKeys: string[] = [],
  delegationTargets: DelegationTargetPromptInput[] = [],
  assignedMcpServerNames: string[] = [],
): string {
  const root = base.trim() || 'You are a helpful AI agent.';
  const fragments = [
    buildSkillPromptFragment(skills),
    buildDelegationPromptFragment(delegationTargets),
    buildConnectedToolsPromptFragment(assignedIntegrationKeys, assignedMcpServerNames),
    buildWorkspacePromptFragment(),
  ].filter(Boolean);
  return fragments.length > 0 ? `${root}\n\n${fragments.join('\n\n')}` : root;
}

/** Clarify which child agents can receive delegated work. */
export function buildDelegationPromptFragment(
  targets: DelegationTargetPromptInput[],
): string {
  if (targets.length === 0) return '';

  const lines = [
    '## Delegation targets',
    '',
    'Use the delegate tool only for specialist child agents listed below.',
    'Perform your own assigned skills directly — skill keys are not delegation targets.',
    '',
  ];

  for (const target of targets) {
    const skillHint =
      target.skillKeys && target.skillKeys.length > 0
        ? ` (skills: ${target.skillKeys.join(', ')})`
        : '';
    lines.push(`- ${target.name}${skillHint}`);
  }

  return lines.join('\n').trimEnd();
}

/** Constrain delegate tool input to known child agent names. */
export function enrichDelegateToolSchema(
  inputSchema: Record<string, unknown>,
  targets: DelegationTargetPromptInput[],
): Record<string, unknown> {
  if (targets.length === 0) return inputSchema;

  const names = targets.map((target) => target.name);
  const properties = {
    ...((inputSchema.properties as Record<string, unknown> | undefined) ?? {}),
    toAgentId: {
      type: 'string',
      description: `Specialist agent name. Valid targets: ${names.join(', ')}. Do not use your own skill keys.`,
      enum: names,
    },
  };

  return { ...inputSchema, properties };
}

/**
 * General preference heuristic: prefer assigned connected tools (integrations / MCP)
 * over low-level coding/shell when both could work. Not phrase→tool routing.
 */
export function buildConnectedToolsPromptFragment(
  assignedIntegrationKeys: string[] = [],
  assignedMcpServerNames: string[] = [],
): string {
  if (assignedIntegrationKeys.length === 0 && assignedMcpServerNames.length === 0) return '';

  const lines = [
    '## Connected tools',
    '',
    'You have assigned integrations and/or MCP servers. Each exposes named tools with',
    'credentials and connection config applied automatically — do not ask the user for tokens',
    'or hunt for secrets.',
    '',
    'When a connected tool can accomplish the task, prefer it over low-level coding/shell tools.',
    'If a connected tool fails or is unavailable, then use coding/shell or other tools.',
    'Honor explicit user or skill instructions even when they ask you to use git, shell, or other low-level tools.',
    '',
  ];

  if (assignedIntegrationKeys.length > 0) {
    lines.push(`Assigned integrations: ${assignedIntegrationKeys.join(', ')}.`);
  }
  if (assignedMcpServerNames.length > 0) {
    lines.push(`Assigned MCP servers: ${assignedMcpServerNames.join(', ')}.`);
  }

  return lines.join('\n').trimEnd();
}

/**
 * Workspace guidance: tells the agent where code lives and how to discover files.
 * Prevents agents from guessing wrong paths like /, /home/user, /workspace.
 */
export function buildWorkspacePromptFragment(): string {
  return `## Workspace

Your file system is sandboxed to a workspace directory. Code repos cloned via \`git clone\`
are stored inside this workspace.

**When working in a cloned repo (local coding tools):**
- Use the \`repoPath\` returned from \`git clone\` (or discover it with \`fs_list_dir\`) for git/fs operations.
- Prefer \`git status\` / \`git branch\` only after you have a valid \`repoPath\` in this workspace.
- Do not assume a repo exists at the workspace root.

**How to find files:**
- Run \`fs_list_dir\` with NO path argument to see what is in the workspace root.
- Use relative paths (e.g. \`"src/components"\`) — they are resolved relative to the workspace.
- Do NOT guess absolute paths like \`/\`, \`/home/user\`, or \`/workspace\` — those are outside the sandbox
  and will always fail with "Path traversal denied".
- Use \`fs_list_dir\` with \`"recursive": true\` to explore the project structure before editing files.`;
}

/** Format assigned skills as a system-prompt fragment for the reasoning loop. */
export function buildSkillPromptFragment(
  skills: SkillPromptInput[],
  _assignedToolKeys: string[] = [],
  _assignedIntegrationKeys: string[] = [],
): string {
  const active = skills.filter(
    (s) => s.instructions.trim() || s.examples.length > 0,
  );
  if (active.length === 0) return '';

  const lines: string[] = [
    '## Assigned skills',
    '',
    'The skills below define reusable competencies. Follow their instructions when relevant.',
    '',
  ];

  for (const skill of active) {
    lines.push(`### ${skill.name} (\`${skill.key}\`)`);
    if (skill.description.trim()) {
      lines.push(skill.description.trim());
      lines.push('');
    }
    if (skill.instructions.trim()) {
      lines.push(skill.instructions.trim());
      lines.push('');
    }
    if (skill.examples.length > 0) {
      lines.push('Examples:');
      for (const ex of skill.examples) {
        lines.push(`User: ${ex.input}`);
        lines.push(`Assistant: ${ex.output}`);
        lines.push('');
      }
    }
  }

  return lines.join('\n').trimEnd();
}
